import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Palette, Layers, Zap, Star, Eye, Edit } from 'lucide-react';

/**
 * CuratorToolsDropdown Component
 * 
 * Provides access to premium curator tools: Display Cards, Wall Planner, and Gallery Experience (3D).
 * Only visible when artworks exist in the collection.
 * 
 * Props:
 * - artworkCount: number (total artworks)
 * - onDisplayCards: function (navigate to Display Cards tool)
 * - onWallPlanner: function (navigate to Wall Planner tool)
 * - onGalleryExperience: function (navigate to 3D Gallery Experience)
 */
const CuratorToolsDropdown = ({
  artworkCount = 0,
  onView,
  onEdit,
  onDisplayCards,
  onWallPlanner,
  onGalleryExperience
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position to keep within viewport
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 280; // min-width from CSS
      const menuHeight = 420; // approximate menu height
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 16; // Buffer from edges

      let right = viewportWidth - buttonRect.right;
      let left = 'auto';
      let top = buttonRect.bottom + 8;
      let bottom = 'auto';
      let maxHeight = 'calc(100vh - 32px)';

      // If menu would be cut off on the right, position it to the left instead
      if (buttonRect.right + menuWidth + padding > viewportWidth) {
        right = 'auto';
        left = Math.max(padding, viewportWidth - buttonRect.left - menuWidth);
      }

      // If menu would be cut off at the bottom, either position it above or limit height
      const spaceBelow = viewportHeight - buttonRect.bottom - padding;
      const spaceAbove = buttonRect.top - padding;

      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        // Position above the button
        top = 'auto';
        bottom = viewportHeight - buttonRect.top + 8;
        maxHeight = `${Math.min(menuHeight, spaceAbove - 8)}px`;
      } else {
        // Position below with limited height if needed
        maxHeight = `${Math.min(menuHeight, spaceBelow - 8)}px`;
      }

      setMenuStyle({
        position: 'fixed',
        top: top !== 'auto' ? top : 'auto',
        bottom: bottom !== 'auto' ? bottom : 'auto',
        left: left !== 'auto' ? left : 'auto',
        right: right !== 'auto' ? right : 'auto',
        maxHeight: maxHeight,
        overflowY: 'auto'
      });
    }
  }, [isOpen]);

  const handleToolClick = (callback) => {
    setIsOpen(false);
    callback();
  };

  return (
    <div className="curator-tools-dropdown" ref={dropdownRef}>
      <button
        ref={buttonRef}
        className="curator-tools-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title="Curator Tools"
      >
        <Palette size={18} />
        <span>Curator Tools</span>
        <ChevronDown size={16} className={`chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <div className="curator-tools-menu" style={menuStyle}>
          {/* View & Edit Section */}
          <button
            className="curator-tool-item"
            onClick={() => handleToolClick(onView)}
            title="View collection public page"
          >
            <div className="curator-tool-icon">
              <Eye size={20} />
            </div>
            <div className="curator-tool-content">
              <span className="curator-tool-name">View</span>
              <span className="curator-tool-description">Public collection page</span>
            </div>
          </button>

          <button
            className="curator-tool-item"
            onClick={() => handleToolClick(onEdit)}
            title="Edit collection details"
          >
            <div className="curator-tool-icon">
              <Edit size={20} />
            </div>
            <div className="curator-tool-content">
              <span className="curator-tool-name">Edit</span>
              <span className="curator-tool-description">Collection settings</span>
            </div>
          </button>

          <div className="curator-tools-divider"></div>

          <div className="curator-tools-header">
            <h3>Curator Tools</h3>
            <span className="curator-tools-subtitle">Premium Features</span>
          </div>

          <button
            className="curator-tool-item"
            onClick={() => handleToolClick(onDisplayCards)}
            title="Create and manage display cards for physical signage"
          >
            <div className="curator-tool-icon">
              <Layers size={20} />
            </div>
            <div className="curator-tool-content">
              <span className="curator-tool-name">Display Cards</span>
              <span className="curator-tool-description">Create printed signage</span>
            </div>
            <span className="curator-tool-badge">Pro</span>
          </button>

          <button
            className="curator-tool-item"
            onClick={() => handleToolClick(onWallPlanner)}
            title="Top-down floor planner with walls, pods, easels and art placement"
          >
            <div className="curator-tool-icon">
              <Palette size={20} />
            </div>
            <div className="curator-tool-content">
              <span className="curator-tool-name">Top Down Planner</span>
              <span className="curator-tool-description">Plan gallery layout</span>
            </div>
            <span className="curator-tool-badge">Pro</span>
          </button>

          <button
            className="curator-tool-item"
            onClick={() => handleToolClick(onGalleryExperience)}
            title="View 3D virtual gallery experience"
          >
            <div className="curator-tool-icon">
              <Zap size={20} />
            </div>
            <div className="curator-tool-content">
              <span className="curator-tool-name">Gallery Experience</span>
              <span className="curator-tool-description">3D virtual gallery</span>
            </div>
            <span className="curator-tool-badge">
              <Star size={14} />
              3D
            </span>
          </button>

          <div className="curator-tools-footer">
            <p className="curator-tools-tip">
              ✨ These premium tools help you visualize and plan your exhibition.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuratorToolsDropdown;
